import React from "react";
import { FileUp, LayoutDashboard, FolderOpen, Settings, ShieldCheck, HelpCircle, Info, FileText, Users, ClipboardCheck, MessageSquare, BarChart3, Landmark } from "lucide-react";
import Logo from "../images/Logo.png";
import { useNavigate } from "react-router-dom";
import { getJwtRole, getJwtIsReviewer } from "../service/AuthService";
import { admin, funder, PEER_REVIEW_ENABLED } from "../constants/Constants";

const navItems = [
    { label: "Dashboard",        mobileLabel: "Home",     icon: LayoutDashboard, path: "/" },
    { label: "Upload Paper",     mobileLabel: "Upload",   icon: FileUp,           path: "/upload" },
    { label: "Submissions",      mobileLabel: "Papers",   icon: FolderOpen,       path: "/submissions" },
    ...(PEER_REVIEW_ENABLED ? [{ label: "Peer Reviews", mobileLabel: "Reviews", icon: Users, path: "/peer-reviews" }] : []),
    { label: "Analytics",        mobileLabel: "Stats",    icon: BarChart3,        path: "/analytics" },
    { label: "Settings",         mobileLabel: "Settings", icon: Settings,         path: "/settings" },
];

export default function Sidebar({ activePage = "Dashboard" }) {
    const navigate = useNavigate();
    const isAdmin = getJwtRole() === admin;
    const isFunder = getJwtRole() === funder;
    const isReviewer = getJwtIsReviewer();

    return (
        <>
            <aside className="sidebar">
                <div className="sidebar-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                    <div className="sidebar-logo-icon">
                        <img src={Logo} alt="Logo" className="sidebar-logo-img theme-invert" />
                    </div>
                    <span className="sidebar-logo-name">DeepThought</span>
                </div>

                <nav className="sidebar-nav">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <div
                                key={item.label}
                                className={`nav-item${item.label === activePage ? " nav-item-active" : ""}`}
                                onClick={() => navigate(item.path)}
                            >
                                <Icon size={14} />
                                {item.label}
                            </div>
                        );
                    })}
                    {PEER_REVIEW_ENABLED && isReviewer && (
                        <div
                            className={`nav-item${activePage === "Review Queue" ? " nav-item-active" : ""}`}
                            onClick={() => navigate("/reviewer")}
                        >
                            <ClipboardCheck size={14} />
                            Review Queue
                        </div>
                    )}
                    {(isAdmin || isFunder) && (
                        <div
                            className={`nav-item${activePage === "Funder Portal" ? " nav-item-active" : ""}`}
                            onClick={() => navigate("/funder")}
                        >
                            <Landmark size={14} />
                            Funder Portal
                        </div>
                    )}
                    {isAdmin && (
                        <div
                            className={`nav-item${activePage === "Admin" ? " nav-item-active" : ""}`}
                            onClick={() => navigate("/admin")}
                        >
                            <ShieldCheck size={14} />
                            Admin
                        </div>
                    )}
                </nav>

                <div className="sidebar-bottom">
                    <div
                        className={`nav-item${activePage === "About" ? " nav-item-active" : ""}`}
                        onClick={() => navigate("/about")}
                    >
                        <Info size={14} />
                        About Us
                    </div>
                    <a
                        className="nav-item"
                        href="/whitepaper.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                    >
                        <FileText size={14} />
                        White Paper
                    </a>
                    <div
                        className={`nav-item${activePage === "FAQ" ? " nav-item-active" : ""}`}
                        onClick={() => navigate("/faq")}
                    >
                        <HelpCircle size={14} />
                        FAQ
                    </div>
                    <div
                        className={`nav-item${activePage === "Feedback" ? " nav-item-active" : ""}`}
                        onClick={() => navigate("/feedback")}
                    >
                        <MessageSquare size={14} />
                        Feedback
                    </div>
                </div>
            </aside>

            <nav className="mobile-nav">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.label === activePage;
                    return (
                        <button
                            key={item.label}
                            className={`mobile-nav-item${isActive ? " mobile-nav-item-active" : ""}`}
                            onClick={() => navigate(item.path)}
                        >
                            <Icon size={22} />
                            <span className="mobile-nav-label">{item.mobileLabel}</span>
                        </button>
                    );
                })}
                {(isAdmin || isFunder) && (
                    <button
                        className={`mobile-nav-item${activePage === "Funder Portal" ? " mobile-nav-item-active" : ""}`}
                        onClick={() => navigate("/funder")}
                    >
                        <Landmark size={22} />
                        <span className="mobile-nav-label">Funder</span>
                    </button>
                )}
                {isAdmin && (
                    <button
                        className={`mobile-nav-item${activePage === "Admin" ? " mobile-nav-item-active" : ""}`}
                        onClick={() => navigate("/admin")}
                    >
                        <ShieldCheck size={22} />
                        <span className="mobile-nav-label">Admin</span>
                    </button>
                )}
            </nav>
        </>
    );
}
